import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Snackbar,
  Tooltip,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import utils from "../../universal/utils";
import {
  collectCertificateSizes,
} from "../../universal/eceCertificates";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    alignItems: "center",
    flexWrap: "wrap",
  },
  addButton: {
    marginLeft: "auto",
  },
  tableContainer: {
    maxHeight: 600,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  dialogContent: {
    minWidth: 500,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  hiddenInput: {
    display: "none",
  },
  uploadRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  pdfFrame: {
    width: "100%",
    height: "70vh",
    border: "none",
    backgroundColor: "#525659",
  },
  chipList: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
  },
  suggestionLabel: {
    display: "block",
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
});

const hebrewLabels = {
  title: "ניהול תעודות ECE",
  filters: {
    eceId: "מספר ECE",
    brand: "יצרן",
    size: "מידה",
    model: "דגם",
    search: "חיפוש",
  },
  table: {
    eceId: "מספר ECE",
    brand: "יצרן",
    size: "מידות",
    rating: "דירוג",
    file: "קובץ",
    goodfor: "מתאים ל",
    actions: "פעולות",
  },
  buttons: {
    add: "הוסף תעודה חדשה",
    edit: "עריכה",
    delete: "מחיקה",
    save: "שמור",
    cancel: "ביטול",
    view: "צפה בתעודה",
    uploadPdf: "העלה PDF",
    replacePdf: "החלף PDF",
    openNewTab: "פתח בחלון חדש",
  },
  dialog: {
    create: "יצירת תעודת ECE חדשה",
    edit: "עריכת תעודת ECE",
    delete: "מחיקת תעודת ECE",
    deleteConfirm: "האם אתה בטוח שברצונך למחוק תעודה זו?",
    viewPdf: "צפייה בתעודת ECE",
  },
  messages: {
    loading: "טוען...",
    noData: "לא נמצאו תעודות",
    success: "הפעולה בוצעה בהצלחה",
    error: "אירעה שגיאה",
    pdfInvalid: "יש להעלות קובץ PDF בלבד",
    pdfTooLarge: "הקובץ גדול מדי (מקסימום 15MB)",
    pdfRequired: "יש להעלות קובץ PDF לתעודה חדשה",
    pdfMissing: "לתעודה זו אין קובץ ב-S3",
    pdfLoadError: "שגיאה בטעינת קובץ התעודה",
    uploading: "מעלה קובץ...",
  },
  fields: {
    eceId: "מספר ECE",
    brand: "יצרן",
    sizes: "מידות",
    sizesHelper: "הזינו מידות מופרדות בפסיקים, או לחצו Enter אחרי כל מידה.",
    rating: "דירוג",
    goodfor: "דגמים (מתאים ל)",
    goodforHelper:
      "הזינו שמות דגמים מופרדים בפסיקים (למשל: Road 5, Road 6, Pilot Street). תעודה אחת יכולה לכסות כמה דגמים מאותו יצרן.",
    pdf: "קובץ התעודה",
  },
};

const emptyCertificate = {
  eceId: "",
  brand: "",
  rating: "",
  s3Path: "",
  sizes: [],
  goodfor: [],
};

const basePath = utils.getEdgeBasePath();
const MAX_PDF_BYTES = 15 * 1024 * 1024;

function uniqueSorted(values) {
  return [...new Set(values.filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, "en", { sensitivity: "base" })
  );
}

function parseTagList(values) {
  const source = Array.isArray(values) ? values : [values];
  const next = [];
  source.forEach((item) => {
    String(item || "")
      .split(/[,;\n]+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .forEach((part) => {
        if (!next.includes(part)) next.push(part);
      });
  });
  return next;
}

const emptyChipInputs = { goodfor: "", sizes: "" };

class EceCertificatesManager extends React.Component {
  constructor(props) {
    super(props);
    this.fileInputRef = React.createRef();
    this.state = {
      certificates: [],
      loading: true,
      saving: false,
      pagination: {
        page: 0,
        limit: 20,
        total: 0,
      },
      filters: {
        eceId: "",
        brand: "",
        size: "",
        model: "",
      },
      dialogOpen: false,
      dialogMode: "create",
      currentCertificate: { ...emptyCertificate },
      chipInputs: { ...emptyChipInputs },
      pdfFileData: null,
      pdfFileName: "",
      deleteDialogOpen: false,
      certificateToDelete: null,
      pdfPreviewOpen: false,
      pdfPreviewUrl: "",
      pdfPreviewLoading: false,
      pdfPreviewCert: null,
      snackbar: {
        open: false,
        message: "",
        severity: "success",
      },
    };
  }

  componentDidMount() {
    this.loadCertificates();
  }

  componentWillUnmount() {
    this.revokePdfPreview();
  }

  authHeaders = () => ({
    "Content-Type": "application/json",
    ...utils.getAdminAuthHeaders(),
  });

  loadCertificates = async () => {
    this.setState({ loading: true });

    try {
      const { pagination, filters } = this.state;
      const queryParams = new URLSearchParams({
        page: pagination.page + 1,
        limit: pagination.limit,
      });

      if (filters.eceId) queryParams.append("eceId", filters.eceId);
      if (filters.brand) queryParams.append("brand", filters.brand);
      if (filters.size) queryParams.append("size", filters.size);
      if (filters.model) queryParams.append("model", filters.model);

      const response = await fetch(
        `${basePath}/api/v1/admin/ece/certificates?${queryParams.toString()}`,
        { headers: utils.getAdminAuthHeaders() }
      );
      const data = await response.json();

      if (data.ok) {
        this.setState({
          certificates: data.certificates,
          pagination: {
            ...pagination,
            total: data.pagination.total,
          },
        });
      }
    } catch (error) {
      console.error("Error loading certificates:", error);
      this.showSnackbar("שגיאה בטעינת התעודות", "error");
    } finally {
      this.setState({ loading: false });
    }
  };

  handlePageChange = (event, newPage) => {
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, page: newPage },
      }),
      () => this.loadCertificates()
    );
  };

  handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, limit: newLimit, page: 0 },
      }),
      () => this.loadCertificates()
    );
  };

  handleFilterChange = (field) => (event) => {
    const value = event.target.value;

    this.setState((prevState) => ({
      filters: {
        ...prevState.filters,
        [field]: value,
      },
    }));
  };

  handleSearch = () => {
    this.setState(
      (prevState) => ({
        pagination: { ...prevState.pagination, page: 0 },
      }),
      () => this.loadCertificates()
    );
  };

  toCertificateForm = (certificate = {}) => ({
    eceId: certificate.eceId || "",
    brand: certificate.brand || "",
    rating: certificate.rating || "",
    s3Path: certificate.s3Path || "",
    sizes: collectCertificateSizes(certificate),
    goodfor: parseTagList(certificate.goodfor),
  });

  openCreateDialog = () => {
    this.setState({
      dialogOpen: true,
      dialogMode: "create",
      currentCertificate: { ...emptyCertificate, sizes: [], goodfor: [] },
      chipInputs: { ...emptyChipInputs },
      pdfFileData: null,
      pdfFileName: "",
    });
  };

  openEditDialog = (certificate) => {
    this.setState({
      dialogOpen: true,
      dialogMode: "edit",
      currentCertificate: this.toCertificateForm(certificate),
      chipInputs: { ...emptyChipInputs },
      pdfFileData: null,
      pdfFileName: "",
    });
  };

  openDeleteDialog = (certificate) => {
    this.setState({
      deleteDialogOpen: true,
      certificateToDelete: certificate,
    });
  };

  closeDialog = () => {
    if (this.state.saving) return;
    this.setState({
      dialogOpen: false,
      chipInputs: { ...emptyChipInputs },
      pdfFileData: null,
      pdfFileName: "",
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  closeDeleteDialog = () => {
    this.setState({ deleteDialogOpen: false, certificateToDelete: null });
  };

  handleFieldChange = (field) => (event) => {
    const value = event.target.value;

    this.setState((prevState) => ({
      currentCertificate: {
        ...prevState.currentCertificate,
        [field]: value,
      },
    }));
  };

  mergePendingTags = (field) =>
    parseTagList([
      ...(this.state.currentCertificate[field] || []),
      this.state.chipInputs[field],
    ]);

  handleChipInputChange = (field, raw) => {
    if (!/[,;\n]/.test(raw)) {
      this.setState((prevState) => ({
        chipInputs: {
          ...prevState.chipInputs,
          [field]: raw,
        },
      }));
      return;
    }

    const trailingSeparator = /[,;\n]\s*$/.test(raw);
    const parts = raw.split(/[,;\n]+/);
    const remainder = trailingSeparator ? "" : parts.pop();
    const complete = parseTagList(parts);

    this.setState((prevState) => ({
      currentCertificate: {
        ...prevState.currentCertificate,
        [field]: parseTagList([
          ...(prevState.currentCertificate[field] || []),
          ...complete,
        ]),
      },
      chipInputs: {
        ...prevState.chipInputs,
        [field]: remainder || "",
      },
    }));
  };

  commitChipInput = (field, extraRaw) => {
    const pending =
      extraRaw !== undefined ? extraRaw : this.state.chipInputs[field];
    const next = parseTagList([
      ...(this.state.currentCertificate[field] || []),
      pending,
    ]);
    this.setState((prevState) => ({
      currentCertificate: {
        ...prevState.currentCertificate,
        [field]: next,
      },
      chipInputs: {
        ...prevState.chipInputs,
        [field]: "",
      },
    }));
    return next;
  };

  addChip = (field, value) => {
    const next = parseTagList([
      ...(this.state.currentCertificate[field] || []),
      value,
    ]);
    this.setState((prevState) => ({
      currentCertificate: {
        ...prevState.currentCertificate,
        [field]: next,
      },
    }));
  };

  removeChip = (field, index) => {
    this.setState((prevState) => {
      const current = [...(prevState.currentCertificate[field] || [])];
      current.splice(index, 1);
      return {
        currentCertificate: {
          ...prevState.currentCertificate,
          [field]: current,
        },
      };
    });
  };

  readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  handlePdfFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    const isPdf =
      file.type === "application/pdf" ||
      String(file.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      this.showSnackbar(hebrewLabels.messages.pdfInvalid, "warning");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      this.showSnackbar(hebrewLabels.messages.pdfTooLarge, "warning");
      event.target.value = "";
      return;
    }

    try {
      const pdfFileData = await this.readFileAsDataUrl(file);
      this.setState({
        pdfFileData,
        pdfFileName: file.name,
      });
    } catch (error) {
      console.error("Error reading PDF:", error);
      this.showSnackbar("שגיאה בקריאת קובץ ה-PDF", "error");
    }
  };

  uploadPdfIfNeeded = async () => {
    const { pdfFileData, pdfFileName, currentCertificate } = this.state;
    if (!pdfFileData) {
      return currentCertificate.s3Path;
    }

    const response = await fetch(`${basePath}/api/v1/admin/ece/certificates/upload`, {
      method: "POST",
      headers: this.authHeaders(),
      body: JSON.stringify({
        fileData: pdfFileData,
        fileName: pdfFileName,
        eceId: currentCertificate.eceId,
        brand: currentCertificate.brand,
        s3Path: currentCertificate.s3Path || undefined,
      }),
    });
    const data = await response.json();
    if (!response.ok || !data.ok) {
      throw new Error(data.message || data.error || "PDF upload failed");
    }
    return data.s3Path;
  };

  handleSave = async () => {
    const { dialogMode, currentCertificate, pdfFileData } = this.state;

    if (!currentCertificate.eceId.trim() || !currentCertificate.brand.trim()) {
      this.showSnackbar("יש למלא מספר ECE ויצרן", "warning");
      return;
    }
    if (dialogMode === "create" && !pdfFileData && !currentCertificate.s3Path) {
      this.showSnackbar(hebrewLabels.messages.pdfRequired, "warning");
      return;
    }

    this.setState({ saving: true });

    try {
      const s3Path = await this.uploadPdfIfNeeded();
      const payload = {
        eceId: currentCertificate.eceId.trim(),
        brand: currentCertificate.brand.trim(),
        rating: currentCertificate.rating,
        s3Path,
        sizes: this.mergePendingTags("sizes"),
        goodfor: this.mergePendingTags("goodfor"),
      };

      let response;
      if (dialogMode === "create") {
        response = await fetch(`${basePath}/api/v1/admin/ece/certificates`, {
          method: "POST",
          headers: this.authHeaders(),
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(
          `${basePath}/api/v1/admin/ece/certificates/${encodeURIComponent(
            currentCertificate.eceId
          )}`,
          {
            method: "PUT",
            headers: this.authHeaders(),
            body: JSON.stringify(payload),
          }
        );
      }

      const data = await response.json();

      if (data.ok) {
        this.showSnackbar(
          dialogMode === "create" ? "התעודה נוצרה בהצלחה" : "התעודה עודכנה בהצלחה",
          "success"
        );
        this.setState(
          {
            saving: false,
            dialogOpen: false,
            chipInputs: { ...emptyChipInputs },
            pdfFileData: null,
            pdfFileName: "",
          },
          () => this.loadCertificates()
        );
      } else {
        this.showSnackbar(data.message || "שגיאה בשמירת התעודה", "error");
        this.setState({ saving: false });
      }
    } catch (error) {
      console.error("Error saving certificate:", error);
      this.showSnackbar(error.message || "שגיאה בשמירת התעודה", "error");
      this.setState({ saving: false });
    }
  };

  handleDelete = async () => {
    const { certificateToDelete } = this.state;

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/ece/certificates/${encodeURIComponent(
          certificateToDelete.eceId
        )}`,
        {
          method: "DELETE",
          headers: utils.getAdminAuthHeaders(),
        }
      );

      const data = await response.json();

      if (data.ok) {
        this.showSnackbar("התעודה נמחקה בהצלחה", "success");
        this.closeDeleteDialog();
        this.loadCertificates();
      } else {
        this.showSnackbar(data.message || "שגיאה במחיקת התעודה", "error");
      }
    } catch (error) {
      console.error("Error deleting certificate:", error);
      this.showSnackbar("שגיאה במחיקת התעודה", "error");
    }
  };

  revokePdfPreview = () => {
    const { pdfPreviewUrl } = this.state;
    if (pdfPreviewUrl) {
      URL.revokeObjectURL(pdfPreviewUrl);
    }
  };

  viewCertificatePdf = async (certificate) => {
    if (!certificate?.s3Path) {
      this.showSnackbar(hebrewLabels.messages.pdfMissing, "warning");
      return;
    }

    this.revokePdfPreview();
    this.setState({
      pdfPreviewOpen: true,
      pdfPreviewLoading: true,
      pdfPreviewUrl: "",
      pdfPreviewCert: certificate,
    });

    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/ece/certificates/${encodeURIComponent(
          certificate.eceId
        )}/file`,
        { headers: utils.getAdminAuthHeaders() }
      );

      if (!response.ok) {
        let message = hebrewLabels.messages.pdfLoadError;
        try {
          const data = await response.json();
          message = data.message || data.error || message;
        } catch (e) {
          // ignore json parse
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const pdfPreviewUrl = URL.createObjectURL(blob);
      this.setState({
        pdfPreviewUrl,
        pdfPreviewLoading: false,
      });
    } catch (error) {
      console.error("Error loading certificate PDF:", error);
      this.setState({ pdfPreviewLoading: false, pdfPreviewOpen: false });
      this.showSnackbar(error.message || hebrewLabels.messages.pdfLoadError, "error");
    }
  };

  closePdfPreview = () => {
    this.revokePdfPreview();
    this.setState({
      pdfPreviewOpen: false,
      pdfPreviewUrl: "",
      pdfPreviewLoading: false,
      pdfPreviewCert: null,
    });
  };

  openPdfInNewTab = () => {
    const { pdfPreviewUrl } = this.state;
    if (pdfPreviewUrl) {
      window.open(pdfPreviewUrl, "_blank", "noopener,noreferrer");
    }
  };

  showSnackbar = (message, severity = "success") => {
    this.setState({
      snackbar: {
        open: true,
        message,
        severity,
      },
    });
  };

  closeSnackbar = () => {
    this.setState((prevState) => ({
      snackbar: {
        ...prevState.snackbar,
        open: false,
      },
    }));
  };

  getModelSuggestions = () => {
    const { certificates, currentCertificate } = this.state;
    const brand = String(currentCertificate.brand || "").trim().toLowerCase();
    const models = [];
    certificates.forEach((cert) => {
      if (brand && String(cert.brand || "").toLowerCase() !== brand) return;
      (cert.goodfor || []).forEach((model) => models.push(model));
    });
    return uniqueSorted(models);
  };

  getSizeSuggestions = () => {
    const { certificates, currentCertificate } = this.state;
    const brand = String(currentCertificate.brand || "").trim().toLowerCase();
    const sizes = [];
    certificates.forEach((cert) => {
      if (brand && String(cert.brand || "").toLowerCase() !== brand) return;
      collectCertificateSizes(cert).forEach((size) => sizes.push(size));
    });
    return uniqueSorted(sizes);
  };

  renderChipField = ({ field, label, helperText, suggestions }) => {
    const { classes } = this.props;
    const { currentCertificate, chipInputs } = this.state;
    const values = currentCertificate[field] || [];
    const unusedSuggestions = (suggestions || []).filter(
      (item) => !values.includes(item)
    );

    return (
      <div className={classes.formField}>
        <TextField
          label={label}
          value={chipInputs[field] || ""}
          helperText={helperText}
          fullWidth
          onChange={(event) => this.handleChipInputChange(field, event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              this.commitChipInput(field);
            }
            if (
              event.key === "Backspace" &&
              !(chipInputs[field] || "") &&
              values.length
            ) {
              this.removeChip(field, values.length - 1);
            }
          }}
          onBlur={() => this.commitChipInput(field)}
          onPaste={(event) => {
            const text = event.clipboardData && event.clipboardData.getData("text");
            if (text && /[,;\n]/.test(text)) {
              event.preventDefault();
              this.commitChipInput(
                field,
                `${chipInputs[field] || ""} ${text}`
              );
            }
          }}
        />
        {values.length > 0 && (
          <Box className={classes.chipList}>
            {values.map((item, index) => (
              <Chip
                key={`${field}-${item}-${index}`}
                label={item}
                size="small"
                className={classes.chip}
                onDelete={() => this.removeChip(field, index)}
              />
            ))}
          </Box>
        )}
        {unusedSuggestions.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              color="textSecondary"
              className={classes.suggestionLabel}
            >
              הצעות קיימות ליצרן זה — לחצו להוספה
            </Typography>
            <Box className={classes.chipList}>
              {unusedSuggestions.slice(0, 12).map((item) => (
                <Chip
                  key={`${field}-suggestion-${item}`}
                  label={item}
                  size="small"
                  variant="outlined"
                  className={classes.chip}
                  onClick={() => this.addChip(field, item)}
                />
              ))}
            </Box>
          </Box>
        )}
      </div>
    );
  };

  render() {
    const { classes } = this.props;
    const {
      certificates,
      loading,
      saving,
      pagination,
      filters,
      dialogOpen,
      dialogMode,
      currentCertificate,
      pdfFileName,
      deleteDialogOpen,
      certificateToDelete,
      pdfPreviewOpen,
      pdfPreviewUrl,
      pdfPreviewLoading,
      pdfPreviewCert,
      snackbar,
    } = this.state;

    return (
      <div className={classes.root}>
        <div className={classes.filterBar}>
          <TextField
            label={hebrewLabels.filters.eceId}
            value={filters.eceId}
            onChange={this.handleFilterChange("eceId")}
            variant="outlined"
            size="small"
          />
          <TextField
            label={hebrewLabels.filters.brand}
            value={filters.brand}
            onChange={this.handleFilterChange("brand")}
            variant="outlined"
            size="small"
          />
          <TextField
            label={hebrewLabels.filters.size}
            value={filters.size}
            onChange={this.handleFilterChange("size")}
            variant="outlined"
            size="small"
          />
          <TextField
            label={hebrewLabels.filters.model}
            value={filters.model}
            onChange={this.handleFilterChange("model")}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            onClick={this.handleSearch}
          >
            {hebrewLabels.filters.search}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={this.openCreateDialog}
            className={classes.addButton}
          >
            {hebrewLabels.buttons.add}
          </Button>
        </div>

        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{hebrewLabels.table.eceId}</TableCell>
                  <TableCell>{hebrewLabels.table.brand}</TableCell>
                  <TableCell>{hebrewLabels.table.size}</TableCell>
                  <TableCell>{hebrewLabels.table.rating}</TableCell>
                  <TableCell>{hebrewLabels.table.file}</TableCell>
                  <TableCell>{hebrewLabels.table.goodfor}</TableCell>
                  <TableCell>{hebrewLabels.table.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {certificates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="textSecondary">
                        {hebrewLabels.messages.noData}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  certificates.map((cert) => (
                    <TableRow key={cert._id || cert.eceId}>
                      <TableCell>{cert.eceId}</TableCell>
                      <TableCell>{cert.brand}</TableCell>
                      <TableCell>
                        <Box>
                          {collectCertificateSizes(cert).map((item) => (
                            <Chip
                              key={`${cert.eceId}-size-${item}`}
                              label={item}
                              size="small"
                              className={classes.chip}
                            />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>{cert.rating}</TableCell>
                      <TableCell>
                        {cert.s3Path ? (
                          <Tooltip title={cert.s3Path}>
                            <Button
                              size="small"
                              color="primary"
                              startIcon={<VisibilityIcon />}
                              onClick={() => this.viewCertificatePdf(cert)}
                            >
                              {hebrewLabels.buttons.view}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          {Array.isArray(cert.goodfor) &&
                            cert.goodfor.map((item) => (
                              <Chip
                                key={`${cert.eceId}-model-${item}`}
                                label={item}
                                size="small"
                                className={classes.chip}
                              />
                            ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => this.openEditDialog(cert)}
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => this.openDeleteDialog(cert)}
                          color="secondary"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TablePagination
          component="div"
          count={pagination.total}
          page={pagination.page}
          onPageChange={this.handlePageChange}
          rowsPerPage={pagination.limit}
          onRowsPerPageChange={this.handleRowsPerPageChange}
          rowsPerPageOptions={[10, 20, 50]}
        />

        <Dialog open={dialogOpen} onClose={this.closeDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {dialogMode === "create"
              ? hebrewLabels.dialog.create
              : hebrewLabels.dialog.edit}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <TextField
              label={hebrewLabels.fields.eceId}
              value={currentCertificate.eceId}
              onChange={this.handleFieldChange("eceId")}
              fullWidth
              className={classes.formField}
              disabled={dialogMode === "edit"}
              helperText="מספר ECE אחד לתעודה. אם אותו מספר מכסה כמה דגמים, ערכו את התעודה הקיימת והוסיפו דגמים."
            />
            <TextField
              label={hebrewLabels.fields.brand}
              value={currentCertificate.brand}
              onChange={this.handleFieldChange("brand")}
              fullWidth
              className={classes.formField}
              helperText="יצרן אחד לתעודה. הוסף את כל הדגמים והמידות הרלוונטיים למטה."
            />
            {this.renderChipField({
              field: "goodfor",
              label: hebrewLabels.fields.goodfor,
              helperText: hebrewLabels.fields.goodforHelper,
              suggestions: this.getModelSuggestions(),
            })}
            {this.renderChipField({
              field: "sizes",
              label: hebrewLabels.fields.sizes,
              helperText: hebrewLabels.fields.sizesHelper,
              suggestions: this.getSizeSuggestions(),
            })}
            <TextField
              label={hebrewLabels.fields.rating}
              value={currentCertificate.rating}
              onChange={this.handleFieldChange("rating")}
              fullWidth
              className={classes.formField}
            />
            <Typography variant="subtitle2" className={classes.formField}>
              {hebrewLabels.fields.pdf}
            </Typography>
            <Box className={classes.uploadRow}>
              <input
                accept="application/pdf,.pdf"
                className={classes.hiddenInput}
                id="ece-certificate-pdf-upload"
                type="file"
                ref={this.fileInputRef}
                onChange={this.handlePdfFileChange}
                disabled={saving}
              />
              <label htmlFor="ece-certificate-pdf-upload">
                <Button
                  variant="outlined"
                  color="primary"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={saving}
                >
                  {dialogMode === "edit" && currentCertificate.s3Path
                    ? hebrewLabels.buttons.replacePdf
                    : hebrewLabels.buttons.uploadPdf}
                </Button>
              </label>
              <Typography variant="body2">
                {pdfFileName || currentCertificate.s3Path || "לא נבחר קובץ"}
              </Typography>
              {dialogMode === "edit" && currentCertificate.s3Path && (
                <Button
                  size="small"
                  color="primary"
                  startIcon={<VisibilityIcon />}
                  onClick={() =>
                    this.viewCertificatePdf({
                      eceId: currentCertificate.eceId,
                      s3Path: currentCertificate.s3Path,
                    })
                  }
                  disabled={saving}
                >
                  {hebrewLabels.buttons.view}
                </Button>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog} disabled={saving}>
              {hebrewLabels.buttons.cancel}
            </Button>
            <Button
              onClick={this.handleSave}
              color="primary"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} /> : null}
            >
              {saving ? hebrewLabels.messages.uploading : hebrewLabels.buttons.save}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={this.closeDeleteDialog}>
          <DialogTitle>{hebrewLabels.dialog.delete}</DialogTitle>
          <DialogContent>
            <Typography>{hebrewLabels.dialog.deleteConfirm}</Typography>
            {certificateToDelete && (
              <Typography variant="body2" style={{ marginTop: 16 }}>
                ECE ID: {certificateToDelete.eceId}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDeleteDialog}>
              {hebrewLabels.buttons.cancel}
            </Button>
            <Button onClick={this.handleDelete} color="secondary" variant="contained">
              {hebrewLabels.buttons.delete}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={pdfPreviewOpen}
          onClose={this.closePdfPreview}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {hebrewLabels.dialog.viewPdf}
            {pdfPreviewCert ? ` — ${pdfPreviewCert.eceId}` : ""}
          </DialogTitle>
          <DialogContent>
            {pdfPreviewLoading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress />
              </div>
            ) : pdfPreviewUrl ? (
              <iframe
                title="ECE certificate PDF"
                src={pdfPreviewUrl}
                className={classes.pdfFrame}
              />
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.openPdfInNewTab}
              color="primary"
              disabled={!pdfPreviewUrl}
            >
              {hebrewLabels.buttons.openNewTab}
            </Button>
            <Button onClick={this.closePdfPreview}>{hebrewLabels.buttons.cancel}</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={this.closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

EceCertificatesManager.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(EceCertificatesManager);
